import { useEffect, useRef, useState } from 'react';
import { Divider } from '../../ui/Divider/Divider';
import { Input } from '../../ui/Input/Input';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { AccountSave } from './AccountSave';
import {
  setName as saveName,
  setLastName as saveLastName,
  setPersonalInfoChanged
} from '../../../store/slices/accountSlice';
import { Toast } from '../../ui/Toast/Toast';
import ModalChangePassword from '../../ui/Modal/ModalChangePassword';
import ModalDeleteAccount from '../../ui/Modal/ModalDeleteAccount';
import Loader from '../../ui/Loader/Loader';
import { getBillingPortal } from '../../../services/BillingService';
import {
  getCurrentUser,
  updateCurrentUser
} from '../../../services/UserService';
import { TextButton } from '../../ui/Button/Buttons';
import { useAccountDefault } from '../../hooks/useAccountDefault';
import { featureFlags } from '../../../config/featureFlags';
import {
  selectAuthUser,
  selectIsPlatformAdmin,
  selectIsSuperAdmin
} from '../../../store/selectors/authSelectors';
import { getPlatformRoleLabel } from '../../../utils/Functions.utils';
import { updateAccount } from '../../../services/AccountDefaultService';

const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ſ\s'-]+$/;
const READ_ONLY_INPUT_CLASS =
  'w-full px-[13px] py-[9px] bg-gray-100 ring-1 ring-gray-200 rounded-md text-gray-900 shadow-sm';

type AccountErrors = {
  firstName: string;
  lastName: string;
  organization: string;
};

const validateName = (value: string, label: 'First name' | 'Last name') => {
  if (!value.trim()) return `${label} is required.`;
  if (!NAME_REGEX.test(value)) {
    return `${label} can only contain letters, spaces, apostrophes, and hyphens.`;
  }
  return '';
};

const validateOrganization = (value: string) =>
  value.trim() ? '' : 'Organization name is required.';

export default function Account() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.account);

  const userRole = useSelector(selectAuthUser)?.role;
  const isSuperAdminRole = useSelector(selectIsSuperAdmin);
  const canEditOrganization = useSelector(selectIsPlatformAdmin);

  const { account, accountLoading } = useSelector(
    (state: RootState) => state.accountDefault
  );
  const { fetchMyAccount } = useAccountDefault();
  const hasFetched = useRef(false);
  const hasRequestedAccount = useRef(false);

  const hasNoPlan = account?.subscription?.plan === 'NO_PLAN';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [billingUrl, setBillingUrl] = useState('');
  const [buttonText, setButtonText] = useState('Save Changes');
  const [errors, setErrors] = useState<AccountErrors>({
    firstName: '',
    lastName: '',
    organization: ''
  });
  const [organization, setOrganization] = useState('');

  const fetchData = async () => {
    hasFetched.current = true;
    setLoading(true);

    try {
      const [billingUrl, currentUser] = await Promise.all([
        getBillingPortal(),
        getCurrentUser()
      ]);

      setBillingUrl(billingUrl.data.url);
      setEmail(currentUser.data.email!);
      setFirstName(currentUser.data.firstName);
      dispatch(saveName(currentUser.data.firstName));
      setLastName(currentUser.data.lastName);
      dispatch(saveLastName(currentUser.data.lastName));
    } catch (error) {
      console.error('Error fetching data: ', error);
    } finally {
      setLoading(false);
    }
  };

  const organizationChanged = Boolean(
    account && canEditOrganization && organization !== account.name
  );

  const handleSave = async () => {
    const nextErrors = {
      firstName: validateName(firstName, 'First name'),
      lastName: validateName(lastName, 'Last name'),
      organization: organizationChanged
        ? validateOrganization(organization)
        : ''
    };

    setErrors(nextErrors);

    if (
      nextErrors.firstName ||
      nextErrors.lastName ||
      nextErrors.organization
    ) {
      return;
    }

    // Trim before it is persisted, matching the organization name below: the
    // name regex permits whitespace, and a stray space survives into the
    // Keycloak name claim and breaks anything that tokenises the full name.
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();

    try {
      setButtonText('Saving...');
      await updateCurrentUser({
        firstName: trimmedFirstName,
        lastName: trimmedLastName
      });

      if (account && organizationChanged) {
        await updateAccount(account.id, { name: organization.trim() });
        await fetchMyAccount();
      }

      setFirstName(trimmedFirstName);
      setLastName(trimmedLastName);
      dispatch(saveName(trimmedFirstName));
      dispatch(saveLastName(trimmedLastName));

      setShowToast(true);
    } catch (error) {
      console.error('Failed to save changes: ', error);
    } finally {
      setButtonText('Save Changes');
    }
  };

  const handleCancel = () => {
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setOrganization(account?.name ?? '');
    setErrors({ firstName: '', lastName: '', organization: '' });
  };

  const handleFirstNameChange = (value: string) => {
    setFirstName(value);
    if (errors.firstName) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        firstName: validateName(value, 'First name')
      }));
    }
  };

  const handleLastNameChange = (value: string) => {
    setLastName(value);
    if (errors.lastName) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        lastName: validateName(value, 'Last name')
      }));
    }
  };

  const handleOrganizationChange = (value: string) => {
    setOrganization(value);
    if (errors.organization) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        organization: validateOrganization(value)
      }));
    }
  };

  const sectionHeader = (title: string, text: string) => {
    return (
      <span className="flex flex-col gap-1 items-start">
        <h2 className="text-zinc-900 font-medium">{title}</h2>
        <p className="text-sm text-gray-500">{text}</p>
      </span>
    );
  };

  useEffect(() => {
    if (account || accountLoading || hasRequestedAccount.current) {
      return;
    }

    hasRequestedAccount.current = true;
    fetchMyAccount();
  }, [account, accountLoading, fetchMyAccount]);

  useEffect(() => {
    // prevent making api calls twice
    if (hasFetched.current) return;

    fetchData();
  }, []);

  useEffect(() => {
    setOrganization(account?.name ?? '');
  }, [account?.name]);

  useEffect(() => {
    if (loading) return;

    dispatch(
      setPersonalInfoChanged(
        firstName !== user.firstName ||
          lastName !== user.lastName ||
          organizationChanged
      )
    );
  }, [
    firstName,
    lastName,
    organizationChanged,
    user.firstName,
    user.lastName,
    loading
  ]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex-1 max-w-none w-full flex flex-col p-4 h-[calc(100vh_-_212px)]">
      {loading ? (
        <div className="absolute top-1/2 left-1/2">
          <Loader />
        </div>
      ) : (
        <div className="overflow-y-auto">
          <div className="flex flex-col gap-5 px-4 py-2">
            {/* Personal information */}
            <div className="flex flex-col gap-6">
              <span className="flex flex-col gap-1 items-start">
                <h2 className="text-zinc-900 font-medium">
                  Personal information
                </h2>
                <p className="text-sm text-gray-500">
                  Manage your basic details.
                </p>
              </span>
              <div className="flex gap-6">
                <div className="flex flex-col gap-6 w-1/2">
                  <Input
                    label="First name"
                    id="name"
                    value={firstName}
                    maxLength={100}
                    onChange={(e) => handleFirstNameChange(e.target.value)}
                    onBlur={() =>
                      setErrors((currentErrors) => ({
                        ...currentErrors,
                        firstName: validateName(firstName, 'First name')
                      }))
                    }
                    error={errors.firstName}
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName="input"
                  />
                  <Input
                    label="Email address"
                    id="email"
                    value={email}
                    maxLength={100}
                    onChange={() => {}}
                    disabled
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName="w-full px-[13px] py-[9px] bg-gray-100 ring-1 ring-gray-200 rounded-md text-gray-900 shadow-sm"
                  />
                </div>
                <div className="w-1/2">
                  <Input
                    label="Last name"
                    id="lastName"
                    value={lastName}
                    maxLength={100}
                    onChange={(e) => handleLastNameChange(e.target.value)}
                    onBlur={() =>
                      setErrors((currentErrors) => ({
                        ...currentErrors,
                        lastName: validateName(lastName, 'Last name')
                      }))
                    }
                    error={errors.lastName}
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName="input"
                  />
                </div>
              </div>
            </div>

            <Divider classes="my-3.5" />

            {/* Organization — a superadmin belongs to none by design, so
                /accounts/me returns account: null for them and there is nothing
                to show but two empty boxes. */}
            <div className="flex flex-col gap-4">
              {sectionHeader(
                account ? 'Organization' : 'Access',
                canEditOrganization
                  ? 'Manage your organization name and view your access role.'
                  : 'View your organization and access role.'
              )}
              {account && (
                <div className="flex gap-6">
                  <div className="w-1/2">
                    <Input
                      id="organization"
                      label="Organization"
                      maxLength={255}
                      onChange={(e) => handleOrganizationChange(e.target.value)}
                      onBlur={() =>
                        setErrors((currentErrors) => ({
                          ...currentErrors,
                          organization: validateOrganization(organization)
                        }))
                      }
                      value={organization}
                      error={errors.organization}
                      containerClassName="flex flex-col items-start gap-1 self-stretch"
                      labelClassName="text-sm text-gray-800 font-medium"
                      inputClassName={
                        canEditOrganization ? 'input' : READ_ONLY_INPUT_CLASS
                      }
                      disabled={!canEditOrganization}
                    />
                  </div>
                  <div className="w-1/2 flex flex-col gap-1">
                    <Input
                      id="organizationId"
                      label="Organization ID"
                      maxLength={255}
                      onChange={() => {}}
                      value={account?.slug ?? ''}
                      containerClassName="flex flex-col items-start gap-1 self-stretch"
                      labelClassName="text-sm text-gray-800 font-medium"
                      inputClassName={READ_ONLY_INPUT_CLASS}
                      disabled
                    />
                    {canEditOrganization && (
                      <p className="text-sm text-gray-500 text-left">
                        This cannot be changed.
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex gap-6">
                <div className="w-1/2">
                  <Input
                    id="role"
                    label="Role"
                    maxLength={255}
                    onChange={() => {}}
                    value={getPlatformRoleLabel(userRole)}
                    containerClassName="flex flex-col items-start gap-1 self-stretch"
                    labelClassName="text-sm text-gray-800 font-medium"
                    inputClassName={READ_ONLY_INPUT_CLASS}
                    disabled
                  />
                </div>
              </div>
            </div>

            <Divider classes="my-3.5" />

            {/* Security */}
            <div className="flex flex-col gap-4">
              {sectionHeader(
                'Security',
                'Manage your password and keep your account secure.'
              )}
              <ModalChangePassword setShowToast={setShowToast} />
            </div>

            {/* Billing & Subscription - hide for now 

            <Divider classes="my-3.5" />
            
            {featureFlags.billingAndSubscriptionEnabled && (
              <>
                <div className="flex flex-col items-start gap-4">
                  {sectionHeader(
                    'Billing & Subscription',
                    'Manage your billing information and subscription plan.'
                  )}
                  {hasNoPlan ? (
                    <TextButton disabled>
                      Manage Billing & Subscription
                    </TextButton>
                  ) : (
                    <a href={billingUrl} target="blank">
                      <TextButton>Manage Billing & Subscription</TextButton>
                    </a>
                  )}
                </div>
              </>
            )} */}

            {/* Delete Account */}
            {!isSuperAdminRole && (
              <>
                <Divider classes="my-3" />

                <div className="flex flex-col gap-4 mb-4">
                  {sectionHeader(
                    'Account',
                    'Permanently delete your account and all data.'
                  )}
                  <ModalDeleteAccount />
                </div>
              </>
            )}

            {showToast && (
              <Toast showToast={showToast} setShowToast={setShowToast} />
            )}
          </div>
        </div>
      )}
      {user.personalInfoChanged && !loading && (
        <AccountSave
          handleSave={handleSave}
          handleCancel={handleCancel}
          disableSave={
            firstName.length === 0 ||
            lastName.length === 0 ||
            (organizationChanged && organization.trim().length === 0) ||
            Boolean(errors.firstName) ||
            Boolean(errors.lastName) ||
            Boolean(errors.organization) ||
            buttonText === 'Saving...'
          }
          buttonText={buttonText}
        />
      )}
    </div>
  );
}
